import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject } from '@angular/core';
import { IApiResponse } from '@web/shared-types';
import { catchError, map, Observable, of } from 'rxjs';
import { z } from 'zod/v4';
import { AppConfigService } from '../../services/app-config.service';
import { ErrorHandlingService } from '../../services/errorHandling/error-handling.service';
import { ValidatorService } from '../../services/validator/validator.service';
import { AuthProviderFactory } from '../../services/auth/auth-provider.factory';
import { AuthProvider } from '../../services/auth/auth.provider';
import { Router } from '@angular/router';
import { authClientForUrl } from '@web/shared-functions/auth';
import { CLIENT_CHANNEL_HEADER, EConfigurationChannel } from '@web/dtos';

export type RequestParams = {
  [param: string]:
    | string
    | number
    | boolean
    | ReadonlyArray<string | number | boolean>;
};

/*
 * The MessageRestcallBuilder is necessary.
 * The normal RestcallBuilder replaces Error Codes with Messages.
 * The MessageRestcallBuilder does not do this as the call would be redundant and cause a circular dependency.
 * Better solution pending.
 * */
export class MessageRestCallBuilder<T, F> {
  private readonly configService: AppConfigService = inject(AppConfigService);
  private http: HttpClient = inject(HttpClient);
  private errorHandlingService: ErrorHandlingService =
    inject(ErrorHandlingService);
  private validatorService: ValidatorService = inject(ValidatorService);
  private authFactory: AuthProviderFactory = inject(AuthProviderFactory);
  private readonly router: Router = inject(Router);

  private get auth(): AuthProvider {
    return this.authFactory.getProvider(authClientForUrl(this.router.url));
  }

  private apiUrl: string | undefined = this.configService.get().general.cmsUrl;

  private observables: Record<
    'entity' | 'download',
    Observable<IApiResponse<any>>
  >;

  private readonly defaultEntity: T;

  constructor(defaultEntity: T, downloadContentEntity: F) {
    this.observables = {
      entity: of({ data: defaultEntity }),
      download: of({ data: downloadContentEntity }),
    };
    this.defaultEntity = defaultEntity;
  }

  getExternal(url: string, params?: RequestParams) {
    this.observables.entity = this.http.get<IApiResponse<T>>(url, {
      headers: this.createAuthorizationHeader({
        clientId: this.configService.get().client.id,
      }),
      params,
    });
    return this;
  }

  putExternal(url: string, body: T, params?: RequestParams) {
    this.observables.entity = this.http.put<IApiResponse<T>>(url, body, {
      headers: this.createAuthorizationHeader({
        clientId: this.configService.get().client.id,
      }),
      params,
    });
    return this;
  }

  deleteExternal(url: string, params?: RequestParams) {
    this.observables.entity = this.http.delete<IApiResponse<T>>(url, {
      headers: this.createAuthorizationHeader({
        clientId: this.configService.get().client.id,
      }),
      params,
    });
    return this;
  }

  get(url: string, params?: RequestParams) {
    this.observables.entity = this.http.get<IApiResponse<T>>(
      this.apiUrl + url,
      {
        headers: this.createAuthorizationHeader(),
        params,
      }
    );
    return this;
  }

  post(url: string, query: T | FormData) {
    this.observables.entity = this.http.post<IApiResponse<T>>(
      this.apiUrl + url,
      query,
      {
        headers: this.createAuthorizationHeader(),
      }
    );
    return this;
  }

  upload(url: string, query: T | FormData) {
    this.observables.entity = this.http.post<IApiResponse<T>>(
      this.apiUrl + url,
      query,
      {
        headers: this.createAuthorizationHeader(),
      }
    );
    return this;
  }

  delete(url: string) {
    this.observables.entity = this.http.delete<IApiResponse<T>>(
      this.apiUrl + url,
      {
        headers: this.createAuthorizationHeader(),
      }
    );
    return this;
  }

  validate(schema: z.ZodType<IApiResponse<T>>) {
    this.observables.entity = this.observables.entity?.pipe(
      map(entity => this.validatorService.validate(entity, schema))
    );
    return this;
  }

  handleErrors() {
    this.observables.entity = this.observables.entity?.pipe(
      catchError(err =>
        this.errorHandlingService.handle<IApiResponse<T>>(err, {
          data: this.defaultEntity,
        })
      )
    );
    return this;
  }

  getDownloadableContent(url: string, params?: RequestParams) {
    this.observables.download = this.http.get<IApiResponse<string>>(
      this.apiUrl + url,
      {
        headers: this.createAuthorizationHeader(),
        params,
      }
    );
    return this;
  }

  private createAuthorizationHeader(additionalHeaders?: {
    [key: string]: string;
  }): HttpHeaders {
    const headers: Record<string, string> = {
      [CLIENT_CHANNEL_HEADER]: EConfigurationChannel.Web,
      ...additionalHeaders,
    };

    if (this.auth.isLoggedIn()) {
      headers['Authorization'] = `Bearer ${this.auth.getAccessToken()}`;
    }

    return new HttpHeaders(headers);
  }

  build() {
    return this.observables.entity;
  }

  buildDownloadContent() {
    return this.observables.download;
  }
}
